import { useEffect, useState } from 'react';
import { Loader2, Search } from 'lucide-react';

import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';

/**
 * Loading state for the /google-ads/weekly/search-terms/review round trip.
 *
 * The Google Ads API does not stream progress. A live review typically takes
 * 30–90 seconds (fetch ~500-1000 search terms + LLM-assisted classification +
 * SERP validation, if enabled). A bare spinner reads as "stuck" after ~5s.
 *
 * The progress bar is HONEST-INDETERMINATE:
 *   - fills quickly for the first ~15 seconds (matches typical fast case)
 *   - slows asymptotically toward 92% after that (never reaches 100% until
 *     the actual response resolves and the parent unmounts this component)
 *   - the underlying math is `92 * (1 - exp(-elapsed / 22))`, tuned so the
 *     bar sits around 50% at 15s and 80% at 45s. Feels like progress; does
 *     not lie about being at "99%" for a minute.
 *
 * The rotating aside is comedic on purpose — set by Chans. It signals to the
 * operator that the wait is expected, not broken, without pretending to know
 * how much longer.
 */

const ASIDES = [
  "Google Ads is thinking. It thinks slowly.",
  "Waiting on Google's API. They charge by the click, we pay in patience.",
  'Every search term below was typed by a real human. Some were... creative.',
  "This usually takes 30 to 90 seconds. Sometimes Google takes a nap.",
  "We're waiting on Google. They're waiting on Google. Everyone's waiting on Google.",
  "If this takes longer than 2 minutes, refresh. Blame Google's servers, not us.",
  'Categorizing search terms. This is the "boring but important" part.',
  "Fun fact: this API predates YouTube. It shows.",
] as const;

const ASIDE_MS = 6_500;
const TICK_MS = 250;
// τ chosen so pct hits ~50% at 15s, ~80% at 45s, asymptote 92%.
const TIME_CONSTANT_S = 22;
const ASYMPTOTE = 92;

export function SearchTermsWaitState({
  windowDays,
  className,
}: {
  windowDays?: string;
  className?: string;
}) {
  const [pct, setPct] = useState(0);
  const [asideIndex, setAsideIndex] = useState(0);

  useEffect(() => {
    const reduce =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      // Reduced motion: skip animation entirely, park the bar at a modest
      // fill so the user still sees "something is happening" without motion.
      setPct(40);
      return undefined;
    }
    const start = Date.now();
    const id = window.setInterval(() => {
      const elapsed = (Date.now() - start) / 1000;
      const next = ASYMPTOTE * (1 - Math.exp(-elapsed / TIME_CONSTANT_S));
      setPct(Math.min(next, ASYMPTOTE));
    }, TICK_MS);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    const reduce =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return undefined;
    const id = window.setInterval(() => {
      setAsideIndex((current) => (current + 1) % ASIDES.length);
    }, ASIDE_MS);
    return () => window.clearInterval(id);
  }, []);

  const stage = windowDays
    ? `Pulling the last ${windowDays} days of search terms from Google Ads…`
    : 'Pulling search terms from Google Ads…';

  return (
    <div
      className={cn(
        'flex flex-col gap-4 rounded-lg border border-coral/20 bg-coral-light/30 px-6 py-6',
        className,
      )}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-coral-light">
          <Search className="h-4 w-4 text-coral" aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex items-center gap-2 text-sm font-medium text-coral-dark">
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
            {stage}
          </div>
          <div className="text-xs text-muted-foreground">
            This can take one to two minutes. We're waiting on Google Ads.
          </div>
        </div>
      </div>
      <Progress
        value={pct}
        className="h-2 bg-coral-light"
        indicatorClassName="bg-coral"
        aria-label="Search term review progress"
      />
      <div className="text-xs italic text-muted-foreground">
        {ASIDES[asideIndex]}
      </div>
    </div>
  );
}
